// frontend/src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { authApi } from '../api/auth';
import { auth, googleProvider } from '../firebase';
import { signInWithPopup, signInWithCustomToken, signOut } from "firebase/auth";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationMessage, setRegistrationMessage] = useState(null);
  const [emailPinId, setEmailPinId] = useState(null);

  // --- Forgotten-password screens ---
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [resetRequestMessage, setResetRequestMessage] = useState(null);
  const [resetRequestError, setResetRequestError] = useState(null);
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);
  const [resetConfirmMessage, setResetConfirmMessage] = useState(null);
  const [resetConfirmError, setResetConfirmError] = useState(null);

  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('accessToken');

      if (storedUser && token) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (e) {
          localStorage.removeItem('user');
          localStorage.removeItem('accessToken');
        }
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  const handleAuthResponse = (response) => {
    if (response.session && response.session.user) {
      const userData = response.session.user;
      const token = response.session.access_token;

      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('accessToken', token);
      if (response.session.refresh_token) {
          localStorage.setItem('refreshToken', response.session.refresh_token);
      }

      setUser(userData);
      setLoading(false);
    } else {
      throw new Error("Invalid response from server");
    }
  };

  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.login(credentials);
      handleAuthResponse(response);
      return { success: true };
    } catch (err) {
      setError(err.message || "Login failed");
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  // onPopupDone (optional) is called once the Google window closes with a
  // chosen account, so the button can switch from "Waiting for Google..."
  // to "Signing you in..." while our server finishes the login.
  const loginWithGoogle = async (onPopupDone) => {
    setLoading(true);
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      if (typeof onPopupDone === 'function') onPopupDone();
      const idToken = await result.user.getIdToken();
      const response = await authApi.socialLogin(idToken);
      handleAuthResponse(response);
      return { success: true };
    } catch (err) {
      console.error("Google Login Error:", err);
      let errorMessage = "Google login failed.";
      if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = "Sign-in popup was closed before completion.";
      } else if (err.code === 'auth/cancelled-popup-request') {
        errorMessage = "Only one sign-in popup can be open at a time.";
      } else if (err.code === 'auth/popup-blocked') {
        errorMessage = "Your browser blocked the Google sign-in window. Please allow pop-ups for this site and try again.";
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = "Could not reach Google. Please check your internet connection and try again.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  };

  // --- PHONE AUTH HELPERS (OTP) ---
  const checkPhone = async (phone) => {
      try {
          const res = await authApi.checkPhone(phone);
          return res.exists;
      } catch (e) {
          console.error(e);
          return false;
      }
  };

  const sendOtp = async (phone) => {
      try {
          const res = await authApi.sendOtp(phone);
          return { success: true, pin_id: res.pin_id };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };

  const verifyOtpAndRegister = async (phone, pinId, otp, password, fullName = null) => {
      setLoading(true);
      try {
          const response = await authApi.verifyOtpAndRegister(phone, pinId, otp, password, fullName);
          // Sign in with the custom token returned
          const userCredential = await signInWithCustomToken(auth, response.custom_token);
          const idToken = await userCredential.user.getIdToken();

          // Finalize login
          const loginResponse = await authApi.socialLogin(idToken);
          handleAuthResponse(loginResponse);
          return { success: true };
      } catch (e) {
          setLoading(false);
          return { success: false, error: e.message };
      }
  };

  // --- EMAIL VERIFICATION (OTP) ---
  const verifyEmailOtp = async (email, pinId, otp) => {
      try {
          const res = await authApi.verifyEmailOtp(email, pinId, otp);
          return { success: true, message: res.message };
      } catch (e) {
          return { success: false, error: e.message };
      }
  };
  // --------------------------

  const register = async (userData) => {
    setLoading(true);
    setError(null);
    setRegistrationMessage(null);
    try {
      const response = await authApi.register(userData);
      setRegistrationMessage(response.message || "Registration successful!");
      setEmailPinId(response.pin_id || null);
      setLoading(false);
      return { success: true, pin_id: response.pin_id || null };
    } catch (err) {
      setError(err.message || "Registration failed");
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  const logout = async () => {
    try {
        await authApi.logout();
        await signOut(auth);
    } catch(e) {}
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.updateUserProfile(profileData);
      if (response.user) {
        setUser(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
        setLoading(false);
        return { success: true, message: response.message };
      } else {
        throw new Error("Failed to update profile data");
      }
    } catch (err) {
      setError(err.message || "Update failed");
      setLoading(false);
      return { success: false, error: err.message };
    }
  };

  // --- FORGOTTEN PASSWORD -------------------------------------------------
  // These three were being called by the Forgot and Reset screens but had
  // never been written, which is why those pages did nothing at all.

  const requestPasswordReset = async (email) => {
    setIsRequestingReset(true);
    setResetRequestMessage(null);
    setResetRequestError(null);
    try {
      const response = await authApi.forgotPassword({ email });
      setResetRequestMessage(response.message);
      setIsRequestingReset(false);
      return { success: true, message: response.message };
    } catch (err) {
      const message = err.message || 'Could not send the reset email.';
      setResetRequestError(message);
      setIsRequestingReset(false);
      return { success: false, error: message };
    }
  };

  const verifyResetToken = async (token) => {
    try {
      const response = await authApi.verifyResetToken(token);
      return { success: !!response.valid, message: response.message };
    } catch (err) {
      return { success: false, error: err.message || 'This reset link is not valid.' };
    }
  };

  const confirmPasswordReset = async (resetData) => {
    setIsConfirmingReset(true);
    setResetConfirmMessage(null);
    setResetConfirmError(null);
    try {
      const response = await authApi.resetPassword(resetData);

      // The old password is gone, so any signed-in session is stale.
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);

      setResetConfirmMessage(response.message);
      setIsConfirmingReset(false);
      return { success: true, message: response.message };
    } catch (err) {
      const message = err.message || 'Password reset failed.';
      setResetConfirmError(message);
      setIsConfirmingReset(false);
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    error,
    setError,
    registrationMessage,
    emailPinId,
    login,
    loginWithGoogle,
    checkPhone,
    sendOtp,
    verifyOtpAndRegister,
    verifyEmailOtp,
    register,
    logout,
    updateProfile,
    requestPasswordReset,
    isRequestingReset,
    resetRequestMessage,
    resetRequestError,
    verifyResetToken,
    confirmPasswordReset,
    isConfirmingReset,
    resetConfirmMessage,
    resetConfirmError,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
